import React from "react";
import { Box, Typography, keyframes } from "@mui/material";

// Import local SVG illustrations
import RetentionIllustration from "../assets/illustrations/Group 1261153141.svg";
import EngagementIllustration from "../assets/illustrations/rafiki.svg";
import RevenueIllustration from "../assets/illustrations/Group 1261153142.svg";

// Progress bar fill animation
const progressFill = keyframes`
  from { width: 0%; }
  to { width: var(--progress-width); }
`;

// Metric counter animation
const metricPop = keyframes`
  0% { transform: scale(0.8); opacity: 0; }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); opacity: 1; }
`;

// Card entrance animation
const cardSlideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Illustration float
const illustrationFloat = keyframes`
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-8px) rotate(1deg); }
`;

const BenefitsSection = () => {

  const benefits = [
    {
      illustration: RetentionIllustration,
      title: "Increased Patient Retention & Engagement",
      description:
        "Smart reports and personalized health insights keep patients engaged with your services, leading to higher retention rates and improved loyalty.",
      metric: "Retention rate",
      value: "+50%",
      progress: 50,
    },
    {
      illustration: EngagementIllustration,
      title: "Automate Follow-ups through WhatsApp/SMS",
      description:
        "Intelligent automation sends timely reminders, health tips, and follow-up recommendations directly to patients via their preferred channels.",
      metric: "Engagement",
      value: "+50%",
      progress: 50,
    },
    {
      illustration: RevenueIllustration,
      title: "Unlock Revenue Insights & Superior Marketing",
      description:
        "Data-driven analytics reveal growth opportunities and optimize your marketing strategies to attract more patients and increase revenue.",
      metrics: [
        { label: "32%", sublabel: "Revenue Growth" },
        { label: "2.4x", sublabel: "ROI Increase" },
      ],
    },
  ];

  return (
    <Box
      sx={{
        width: "100%",
        padding: {
          xs: "40px 24px",
          md: "50px 80px",
        },
        backgroundColor: "transparent",
        position: "relative",
      }}
    >
      {/* Section Header - Enhanced */}
      <Box
        sx={{
          textAlign: "center",
          maxWidth: "800px",
          margin: { xs: "0 auto 24px", md: "0 auto 32px" },
          position: "relative",
          zIndex: 1,
        }}
      >
        <Typography
          sx={{
            fontFamily: "Poppins, sans-serif",
            fontSize: { xs: "24px", md: "38px" },
            fontWeight: 600,
            lineHeight: { xs: "34px", md: "52px" },
            color: "#0f172a",
            marginBottom: { xs: "12px", md: "16px" },
            letterSpacing: "-0.5px",
          }}
        >
          Why Leading Labs & Hospitals{" "}
          <Box 
            component="span" 
            sx={{ 
              background: "linear-gradient(135deg, #3567D6 0%, #5B8DEF 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              position: "relative",
            }}
          >
            Choose Us
          </Box>
        </Typography>
        <Typography
          sx={{
            fontFamily: "Inter, sans-serif",
            fontSize: { xs: "14px", md: "17px" },
            fontWeight: 400,
            lineHeight: { xs: "22px", md: "28px" },
            color: "#64748b",
            textAlign: "center",
            maxWidth: "600px",
            margin: "0 auto",
          }}
        >
          Transform your diagnostic services with intelligent automation and
          patient engagement
        </Typography>
      </Box>

      {/* Trust Badges - Enhanced with animations */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: "12px", md: "20px" },
          justifyContent: "center",
          marginBottom: { xs: "50px", md: "64px" },
          flexWrap: "wrap",
          position: "relative",
          zIndex: 1,
        }}
      >
        {[
          { label: "HIPAA Compliant", icon: "🔒" },
          { label: "AWS Powered", icon: "☁️" },
          { label: "Fully Customizable", icon: "⚙️" },
        ].map((badge, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              backgroundColor: "rgba(255,255,255,0.95)",
              borderRadius: "14px",
              padding: { xs: "12px 20px", md: "14px 24px" },
              boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.04)",
              border: "1px solid rgba(64, 123, 255, 0.08)",
              minWidth: { xs: "100%", sm: "auto" },
              justifyContent: { xs: "center", sm: "flex-start" },
              cursor: "default",
              transition: "all 0.3s ease",
              animation: `${cardSlideUp} 0.6s ease-out ${index * 0.1}s both`,
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: "0px 8px 24px rgba(64, 123, 255, 0.12)",
                borderColor: "rgba(64, 123, 255, 0.2)",
              },
            }}
          >
            <Box
              sx={{
                width: "32px",
                height: "32px",
                borderRadius: "10px",
                background: "linear-gradient(135deg, rgba(64, 123, 255, 0.1) 0%, rgba(91, 141, 239, 0.15) 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                fontSize: "16px",
              }}
            >
              {badge.icon}
            </Box>
            <Typography
              sx={{
                fontFamily: "Poppins, sans-serif",
                fontSize: { xs: "14px", md: "15px" },
                fontWeight: 600,
                lineHeight: { xs: "20px", md: "24px" },
                color: "#1e293b",
              }}
            >
              {badge.label}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Benefits Cards - Enhanced with animations */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: { xs: "24px", md: "28px" },
          justifyContent: "center",
          width: "100%",
          maxWidth: "1280px",
          margin: "0 auto",
          position: "relative",
          zIndex: 1,
        }}
      >
        {benefits.map((benefit, index) => (
          <Box
            key={index}
            sx={{
              width: { xs: "100%", md: "32%" },
              maxWidth: { md: "380px" },
              backgroundColor: "rgba(255,255,255,0.98)",
              borderRadius: "24px",
              padding: { xs: "24px", md: "28px" },
              boxShadow: "0px 4px 24px rgba(0, 0, 0, 0.06)",
              border: "1px solid rgba(64, 123, 255, 0.06)",
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
              animation: `${cardSlideUp} 0.7s ease-out ${0.2 + index * 0.15}s both`,
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "4px",
                background: index === 0 
                  ? "linear-gradient(90deg, #10b981 0%, #34d399 100%)"
                  : index === 1 
                  ? "linear-gradient(90deg, #3567D6 0%, #5B8DEF 100%)"
                  : "linear-gradient(90deg, #f59e0b 0%, #fbbf24 100%)",
                opacity: 0,
                transition: "opacity 0.3s ease",
              },
              "&:hover": {
                transform: "translateY(-8px)",
                boxShadow: "0px 16px 48px rgba(64, 123, 255, 0.12)",
                borderColor: "rgba(64, 123, 255, 0.15)",
                "&::before": {
                  opacity: 1,
                },
                "& .benefit-illustration": {
                  animation: `${illustrationFloat} 2s ease-in-out infinite`,
                },
              },
            }}
          >
            {/* Illustration */}
            <Box
              sx={{
                width: "100%",
                height: { xs: "140px", md: "150px" },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "linear-gradient(135deg, #f8faff 0%, #eef4ff 100%)",
                borderRadius: "16px",
                padding: { xs: "16px", md: "20px" },
                position: "relative",
                overflow: "hidden",
              }}
            >
              <Box
                className="benefit-illustration"
                component="img"
                src={benefit.illustration}
                alt={benefit.title}
                sx={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                  transition: "transform 0.3s ease",
                }}
              />
            </Box>

            {/* Title */}
            <Typography
              sx={{
                fontFamily: "Poppins, sans-serif",
                fontSize: { xs: "18px", md: "20px" },
                fontWeight: 600,
                lineHeight: { xs: "24px", md: "28px" },
                color: "#0f172a",
                marginTop: "4px",
              }}
            >
              {benefit.title}
            </Typography>

            {/* Description */}
            <Typography
              sx={{
                fontFamily: "Inter, sans-serif",
                fontSize: { xs: "13px", md: "14px" },
                fontWeight: 400,
                lineHeight: { xs: "20px", md: "22px" },
                color: "#64748b",
                flex: 1,
              }}
            >
              {benefit.description}
            </Typography>

            {/* Metrics */}
            {benefit.metrics ? (
              <Box
                sx={{
                  display: "flex",
                  gap: { xs: "10px", md: "12px" },
                  marginTop: "auto",
                }}
              >
                {benefit.metrics.map((metric, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      flex: 1,
                      background: "linear-gradient(135deg, #f0f7ff 0%, #e8f1ff 100%)",
                      borderRadius: "14px",
                      padding: { xs: "14px 12px", md: "16px 14px" },
                      border: "1px solid rgba(64, 123, 255, 0.1)",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      transition: "all 0.3s ease",
                      cursor: "default",
                      "&:hover": {
                        transform: "scale(1.03)",
                        boxShadow: "0 4px 12px rgba(64, 123, 255, 0.1)",
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Poppins, sans-serif",
                        fontSize: { xs: "20px", md: "24px" },
                        fontWeight: 700,
                        lineHeight: { xs: "24px", md: "28px" },
                        background: "linear-gradient(135deg, #3567D6 0%, #5B8DEF 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        backgroundClip: "text",
                        marginBottom: "4px",
                        animation: `${metricPop} 0.5s ease-out ${0.5 + idx * 0.2}s both`,
                      }}
                    >
                      {metric.label}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: "Inter, sans-serif",
                        fontSize: { xs: "11px", md: "12px" },
                        fontWeight: 500,
                        lineHeight: { xs: "14px", md: "16px" },
                        color: "#64748b",
                      }}
                    >
                      {metric.sublabel}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Box
                sx={{
                  marginTop: "auto",
                  backgroundColor: "#f8fafc",
                  borderRadius: "14px",
                  padding: "14px 16px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "10px",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "Inter, sans-serif",
                      fontSize: { xs: "13px", md: "14px" },
                      fontWeight: 500,
                      color: "#475569",
                    }}
                  >
                    {benefit.metric}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "Poppins, sans-serif",
                      fontSize: { xs: "16px", md: "18px" },
                      fontWeight: 700,
                      background: "linear-gradient(135deg, #10b981 0%, #34d399 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                    }}
                  >
                    {benefit.value}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: "100%",
                    height: "8px",
                    backgroundColor: "#e2e8f0",
                    borderRadius: "4px",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      "--progress-width": `${benefit.progress}%`,
                      width: `${benefit.progress}%`,
                      height: "100%",
                      background: "linear-gradient(90deg, #10b981 0%, #34d399 100%)",
                      borderRadius: "4px",
                      animation: `${progressFill} 1.5s ease-out 0.5s both`,
                      boxShadow: "0 0 8px rgba(16, 185, 129, 0.4)",
                    }}
                  />
                </Box>
              </Box>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default BenefitsSection;

